import { useEffect, useRef, useCallback } from 'react';
import { useLocation, useHistory } from 'react-router-dom';

import { useFileManagerContext } from 'modules/FileManagerV2/context/FileManagerContext';
import { useActivityLogPreview } from 'hooks/useActivityLogPreview';
import { getTenantDetails, getValueFromCookies, removeValueFromCookies, setAppTitle } from 'utils/utils';
import { clearHeicCache } from 'utils/heicUtils';

/**
 * Encapsulates all business logic for PreviewModal:
 * - Activity logging (decoupled from getFiles)
 * - getFiles refresh (called once on close only)
 * - URL param management (file_id)
 * - App title management
 * - Auto time-entry cleanup (onMountUnMount / onOperationBreak)
 */
export function usePreviewModal() {
  const {
    modals,
    previewList,
    selectedPreviewItem,
    setSelectedPreviewItem,
    versionFileUpload,
    onMountUnMount,
    onOperationBreak,
    isClientFileManager,
    isGlobalFileManager,
    fileManagerFilters,
    caseDetails,
    query,
    getFiles,
    currentView,
    pageLimit,
  } = useFileManagerContext();

  const { showPreview, setShowPreview } = modals;
  const { addActivity } = useActivityLogPreview();
  const location = useLocation();
  const history = useHistory();

  // Refs to always hold latest values in cleanup — avoids stale closure bug
  const latestFiltersRef = useRef(fileManagerFilters);
  const latestCaseDetailsRef = useRef(caseDetails);
  const latestOnMountUnMountRef = useRef(onMountUnMount);
  const latestOnOperationBreakRef = useRef(onOperationBreak);

  useEffect(() => {
    latestFiltersRef.current = fileManagerFilters;
  }, [fileManagerFilters]);
  useEffect(() => {
    latestCaseDetailsRef.current = caseDetails;
  }, [caseDetails]);
  useEffect(() => {
    latestOnMountUnMountRef.current = onMountUnMount;
  }, [onMountUnMount]);
  useEffect(() => {
    latestOnOperationBreakRef.current = onOperationBreak;
  }, [onOperationBreak]);

  // App tab title
  useEffect(() => {
    setAppTitle('File Manager', selectedPreviewItem?.display_name || '');
    return () => setAppTitle('File Manager');
  }, [selectedPreviewItem]);

  // Free converted HEIC blob URLs when the modal closes
  useEffect(() => {
    return () => clearHeicCache();
  }, []);

  // Auto time-entry cleanup on unmount
  useEffect(() => {
    return () => {
      const filters = latestFiltersRef.current;
      const details = latestCaseDetailsRef.current;
      const mountFn = latestOnMountUnMountRef.current;
      const breakFn = latestOnOperationBreakRef.current;

      if (filters?.case?.id || (details?.case_id && !details?.is_lead)) {
        typeof mountFn === 'function' && mountFn('file-preview', 'unmounted', filters?.case?.id || details?.case_id);
      }
      setTimeout(() => {
        typeof breakFn === 'function' && breakFn();
      }, 3000);
    };
  }, []);

  /**
   * Builds the payload object for getFiles API calls.
   * Single source of truth — was duplicated in two places in V1.
   */
  const buildGetFilesObj = useCallback(() => {
    const obj = {
      case_id: isGlobalFileManager || isClientFileManager ? fileManagerFilters?.case?.id || '' : caseDetails?.case_id,
      selectedFolder: fileManagerFilters?.selectedFolder,
      selectedType: fileManagerFilters?.fileType,
      tag: fileManagerFilters?.tag,
      query,
      folder_id: fileManagerFilters?.selectedFolder,
      is_read: isGlobalFileManager ? fileManagerFilters?.is_read : fileManagerFilters?.fileStatus,
    };

    if (isClientFileManager) {
      if (fileManagerFilters?.shared_folder) {
        obj.shared_folder = fileManagerFilters.shared_folder;
      }
      if (
        fileManagerFilters?.shared_contact_list &&
        Array.isArray(fileManagerFilters.shared_contact_list) &&
        fileManagerFilters.shared_contact_list.length > 0
      ) {
        obj.shared_contact_list = fileManagerFilters.shared_contact_list;
      }
      if (fileManagerFilters?.folder_shared === true) {
        obj.folder_shared = fileManagerFilters.folder_shared;
      }
    }

    return obj;
  }, [isGlobalFileManager, isClientFileManager, fileManagerFilters, caseDetails?.case_id, query]);

  const removeQueryParam = useCallback(() => {
    const searchParams = new URLSearchParams(location.search);
    searchParams.delete('file_id');
    history.push({ pathname: location.pathname, search: searchParams.toString() });
    if (getValueFromCookies('redirect_url')) {
      removeValueFromCookies('redirect_url');
    }
  }, [location, history]);

  const handleSlideChange = useCallback(
    (item) => {
      const fileId = item?.object_id;
      const searchParams = new URLSearchParams(location.search);
      searchParams.set('file_id', fileId);
      history.push({ pathname: location.pathname, search: searchParams.toString() });
    },
    [location, history]
  );

  /**
   * Logs a document-read activity event.
   * Deliberately decoupled from getFiles — refreshing the file list
   * on every file view was causing excessive API traffic in V1.
   */
  const createActivityLog = useCallback(
    (item) => {
      setTimeout(() => {
        const [tenantId] = getTenantDetails();
        const stateDetails = location?.state?.case_details;
        const data = {
          event_key: 'Read',
          event_type: 'document',
          event_subtype: 'read_document',
          message: `Viewed Document: ${item?.display_name ?? stateDetails?.display_name}`,
          event_status: 'success',
          pk: item?.pk ?? stateDetails?.pk,
          sk: item?.sk ?? stateDetails?.sk,
          event_items: {
            case_id: item?.case_id ?? stateDetails?.case_id ?? '',
            tenant_id: tenantId,
            case_name: item?.case_name ?? stateDetails?.case_name ?? '',
            is_lead: item?.is_lead ?? stateDetails?.is_lead,
          },
        };

        addActivity(data).catch((err) => {
          console.error('Error posting activity log:', err);
        });
      }, 1000);

      // Auto time-entry mount signal
      if (!item?.is_lead) {
        typeof onMountUnMount === 'function' &&
          onMountUnMount('file-preview', 'mounted', fileManagerFilters?.case?.id || caseDetails?.case_id);
      }
    },
    [addActivity, location?.state, onMountUnMount, fileManagerFilters, caseDetails]
  );

  const closePreviewModal = useCallback(() => {
    removeQueryParam();
    setShowPreview(false);
    setSelectedPreviewItem();
    // Single getFiles call — only on modal close, not on every file view
    getFiles(buildGetFilesObj(), currentView, pageLimit);
  }, [removeQueryParam, setShowPreview, setSelectedPreviewItem, getFiles, buildGetFilesObj, currentView, pageLimit]);

  return {
    showPreview,
    selectedPreviewItem,
    setSelectedPreviewItem,
    previewList,
    versionFileUpload,
    isClientFileManager,
    createActivityLog,
    handleSlideChange,
    closePreviewModal,
  };
}
